import React, { useState, useEffect, useRef } from "react";
import "./ImageResizer.css";

/* ---------- Helpers ---------- */
const toPx = (v, unit, dpi) => {
  v = Number(v);
  if (unit === "px") return v;
  if (unit === "inch") return v * dpi;
  if (unit === "cm") return (v / 2.54) * dpi;
  if (unit === "mm") return (v / 25.4) * dpi;
  return v;
};

const getSizeKB = (dataUrl) => ((dataUrl.length * 0.75) / 1024).toFixed(0);

const compressToLimit = (canvas, format, maxKB) => {
  let q = 0.9, out;
  do {
    out = canvas.toDataURL(`image/${format === "jpg" || format === "jpeg" ? "jpeg" : format}`, q);
    q -= 0.05;
  } while ((out.length * 0.75) / 1024 > maxKB && q > 0.1);
  return out;
};

const checkReady = (size, min = 10, max) => {
  if (!size) return "idle";
  if (size < min) return "low";
  if (size > max) return "high";
  return "ok";
};

export default function ImageResizer() {
  const [image, setImage] = useState(null);
  const [processed, setProcessed] = useState(null);
  const [originalSize, setOriginalSize] = useState(0);

  const [advancedMode, setAdvancedMode] = useState(false);

  const [unit, setUnit] = useState("cm");
  const [width, setWidth] = useState(3.5);
  const [height, setHeight] = useState(4.5);
  const [dpi, setDpi] = useState(200);

  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);

  const [format, setFormat] = useState("jpeg");
  const [maxSizeKB, setMaxSizeKB] = useState(100);

  const [crop, setCrop] = useState(null);
  const [action, setAction] = useState(null);
  const [startPos, setStartPos] = useState(null);

  const [zoom, setZoom] = useState(1);
  const [lockRatio, setLockRatio] = useState(true);
  const [ratioPreset, setRatioPreset] = useState("passport");
  const [snapEnabled, setSnapEnabled] = useState(true);

  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });

  const baseRef = useRef(null);
  const previewRef = useRef(null);
  const imgRef = useRef(null);

  /* ---------- Upload ---------- */
  const handleUpload = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setOriginalSize((f.size / 1024).toFixed(0));
    const r = new FileReader();
    r.onload = (ev) => setImage(ev.target.result);
    r.readAsDataURL(f);
  };

  /* ---------- Load ---------- */
  useEffect(() => {
    if (!image) return;
    const img = new Image();
    img.src = image;
    img.onload = () => {
      imgRef.current = img;
      setCrop({
        x: img.width * 0.25,
        y: img.height * 0.25,
        w: img.width * 0.5,
        h: img.height * 0.5,
      });
    };
  }, [image]);

  /* ---------- Preset Apply ---------- */
  useEffect(() => {
    if (!imgRef.current || ratioPreset === "custom") return;

    const img = imgRef.current;

    let ratio = null;
    if (lockRatio) {
      if (ratioPreset === "passport") ratio = 3.5 / 4.5;
      else if (ratioPreset === "square") ratio = 1;
      else if (ratioPreset === "portrait") ratio = 3 / 4;
      else ratio = width / height;
    }

    if (!ratio) return;

    const w = img.width * 0.5;
    const h = w / ratio;

    setCrop({
      x: (img.width - w) / 2,
      y: (img.height - h) / 2,
      w,
      h,
    });

  }, [ratioPreset, lockRatio, width, height]);

  /* ---------- Pointer ---------- */
  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (canvas.width / r.width),
      y: (e.clientY - r.top) * (canvas.height / r.height),
    };
  };

  const getAction = (x, y, c) => {
    if (!c) return "pan";

    const edge = 10;
    const L = Math.abs(x - c.x) < edge;
    const R = Math.abs(x - (c.x + c.w)) < edge;
    const T = Math.abs(y - c.y) < edge;
    const B = Math.abs(y - (c.y + c.h)) < edge;

    if (L && T) return "nw";
    if (R && T) return "ne";
    if (L && B) return "sw";
    if (R && B) return "se";
    if (T) return "n";
    if (B) return "s";
    if (L) return "w";
    if (R) return "e";

    if (x > c.x && x < c.x + c.w && y > c.y && y < c.y + c.h)
      return "move";

    return "pan";
  };

  const cursorMap = {
    nw: "nwse-resize",
    se: "nwse-resize",
    ne: "nesw-resize",
    sw: "nesw-resize",
    n: "ns-resize",
    s: "ns-resize",
    e: "ew-resize",
    w: "ew-resize",
    move: "move",
    pan: "grab",
  };

  const handleDown = (e) => {
    if (!baseRef.current) return;
    const pos = getPos(e, baseRef.current);
    setAction(getAction(pos.x, pos.y, crop));
    setStartPos(pos);
  };

  const handleMove = (e) => {
    if (!crop || !baseRef.current) return;

    const canvas = baseRef.current;
    let pos = getPos(e, canvas);
    const act = action || getAction(pos.x, pos.y, crop);
    canvas.style.cursor = cursorMap[act];

    if (!action || !startPos) return;

    const dx = pos.x - startPos.x;
    const dy = pos.y - startPos.y;

    let newCrop = { ...crop };

    if (act === "move") {
      let nx = crop.x + dx;
      let ny = crop.y + dy;

      if (snapEnabled && imgRef.current) {
        const cx = imgRef.current.width / 2;
        const cy = imgRef.current.height / 2;

        if (Math.abs(nx + crop.w / 2 - cx) < 20) nx = cx - crop.w / 2;
        if (Math.abs(ny + crop.h / 2 - cy) < 20) ny = cy - crop.h / 2;
      }

      newCrop.x = nx;
      newCrop.y = ny;
    }

    if (act.includes("e")) newCrop.w += dx;
    if (act.includes("s")) newCrop.h += dy;
    if (act.includes("w")) {
      newCrop.x += dx;
      newCrop.w -= dx;
    }
    if (act.includes("n")) {
      newCrop.y += dy;
      newCrop.h -= dy;
    }

    if (lockRatio) {
      let ratio = ratioPreset === "passport" ? 3.5 / 4.5 :
                  ratioPreset === "square" ? 1 :
                  ratioPreset === "portrait" ? 3 / 4 :
                  width / height;
      newCrop.h = newCrop.w / ratio;
    }

    setCrop(newCrop);
    setStartPos(pos);
  };

  const handleUp = () => {
    setAction(null);
    setStartPos(null);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom((z) => Math.max(1, Math.min(3, z + (e.deltaY < 0 ? 0.1 : -0.1))));
  };

  /* ---------- Draw ---------- */
  useEffect(() => {
    if (!imgRef.current || !baseRef.current) return;

    const img = imgRef.current;
    const canvas = baseRef.current;
    const ctx = canvas.getContext("2d");

    canvas.width = img.width;
    canvas.height = img.height;

    ctx.drawImage(img, imgOffset.x, imgOffset.y, img.width * zoom, img.height * zoom);

    if (advancedMode && crop) {
      ctx.fillStyle = "rgba(0,0,0,0.6)";
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(crop.x, crop.y, crop.w, crop.h);
      ctx.fill("evenodd");

      ctx.strokeStyle = "#00ffcc";
      ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);
    }
  }, [crop, zoom, imgOffset, advancedMode]);

  /* ---------- Preview ---------- */
  useEffect(() => {
    if (!imgRef.current || !previewRef.current) return;

    const img = imgRef.current;
    const canvas = previewRef.current;
    const ctx = canvas.getContext("2d");

    const w = Math.round(toPx(width, unit, dpi));
    const h = Math.round(toPx(height, unit, dpi));

    canvas.width = w;
    canvas.height = h;

    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;

    if (!advancedMode || !crop) {
      ctx.drawImage(img, 0, 0, w, h);
    } else {
      ctx.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, w, h);
    }

    const output = compressToLimit(canvas, format, maxSizeKB);
    const size = Number(getSizeKB(output));

    setProcessed({
      url: output,
      size,
      status: checkReady(size, 10, maxSizeKB),
    });
  }, [crop, width, height, unit, dpi, brightness, contrast, saturation, format, maxSizeKB, advancedMode]);

  const autoFit = () => {
  if (!previewRef.current) return;

  const canvas = previewRef.current;
  let q = 0.9;
  let out;

  do {
    out = canvas.toDataURL(`image/${format === "jpg" || format === "jpeg" ? "jpeg" : format}`, q);
    const sizeKB = (out.length * 0.75) / 1024;

    if (sizeKB > maxSizeKB) {
      q -= 0.05;
    } else {
      break;
    }
  } while (q > 0.1);

  const finalSize = Number(((out.length * 0.75) / 1024).toFixed(0));

  setProcessed({
    url: out,
    size: finalSize,
    status: checkReady(finalSize, 10, maxSizeKB),
  });
};

  /* ---------- Reset ---------- */
  const handleReset = () => {
    if (!imgRef.current) return;

    const img = imgRef.current;

    setCrop({
      x: img.width * 0.25,
      y: img.height * 0.25,
      w: img.width * 0.5,
      h: img.height * 0.5,
    });

    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setZoom(1);
    setImgOffset({ x: 0, y: 0 });
    setMaxSizeKB(100);
    setLockRatio(true);
    setRatioPreset("passport");
    setSnapEnabled(true);
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = processed.url;
    a.download = `photo.${format}`;
    a.click();
  };

  return (
    <div className="container">
      <h2>📸 Image Resizing Tool</h2>

      <label> Browse Photo:
        <input type="file" onChange={handleUpload} />
      </label>
      &nbsp;
      <label>
        <input type="checkbox" checked={advancedMode} onChange={(e)=>setAdvancedMode(e.target.checked)} />
        Advanced Mode
      </label>
      &nbsp;
      <div className="controls">
        <label>Unit:
        <select value={unit} onChange={(e)=>setUnit(e.target.value)}>
          <option value="px">px</option>
          <option value="cm">cm</option>
          <option value="mm">mm</option>
          <option value="inch">inch</option>
        </select> </label>
        &nbsp;
        <label>Width:
          <input value={width} onChange={(e)=>setWidth(Number(e.target.value))} />
        </label>
        &nbsp;
        <label>Height:
          <input value={height} onChange={(e)=>setHeight(Number(e.target.value))} />
        </label>
        &nbsp;
        <label>DPI:
          <input value={dpi} onChange={(e)=>setDpi(Number(e.target.value))} />
        </label>
          &nbsp;
        <label>Required Format:<select value={format} onChange={(e)=>setFormat(e.target.value)}>
          
          <option value="jpeg">JPEG</option>
          <option value="jpg">JPG</option>
          <option value="png">PNG</option>
          <option value="webp">WEBP</option>
        </select> </label>
        &nbsp;
        <label>Expected Max Size (KB):
          <input value={maxSizeKB} onChange={(e)=>setMaxSizeKB(Number(e.target.value))} />
        </label>
      </div>

      {advancedMode && (
        <>
          <label>Brightness: {brightness}%</label>
          <input type="range" min="50" max="150" value={brightness} onChange={(e)=>setBrightness(Number(e.target.value))} />

          <label>Contrast: {contrast}%</label>
          <input type="range" min="50" max="150" value={contrast} onChange={(e)=>setContrast(Number(e.target.value))} />

          <label>Saturation: {saturation}%</label>
          <input type="range" min="50" max="150" value={saturation} onChange={(e)=>setSaturation(Number(e.target.value))} />

          <label>Zoom: {zoom.toFixed(1)}x</label>
          <input type="range" min="1" max="3" step="0.1" value={zoom} onChange={(e)=>setZoom(Number(e.target.value))} />

          {/* <label>Max Size (KB): {maxSizeKB}</label> 
          <input type="range" min="10" max="200" value={maxSizeKB} onChange={(e)=>setMaxSizeKB(Number(e.target.value))} />*/}

          <select value={ratioPreset} onChange={(e)=>setRatioPreset(e.target.value)}>
            <option value="passport">Passport</option>
            <option value="square">Square</option>
            <option value="portrait">Portrait</option>
            <option value="custom">Custom</option>
          </select>

          <label>
            <input type="checkbox" checked={snapEnabled} onChange={(e)=>setSnapEnabled(e.target.checked)} />
            Snap to Center
          </label>

          <label>
            <input type="checkbox" checked={lockRatio} onChange={(e)=>setLockRatio(e.target.checked)} />
            Lock Ratio
          </label>
        </>
      )}

      <div className="editor">
        <div>
          <h3>Original ({originalSize} KB)</h3>
          <canvas ref={baseRef} onMouseDown={handleDown} onMouseMove={handleMove} onMouseUp={handleUp} onWheel={handleWheel} />
        </div>

        <div>
          <h3>Processed ({processed?.size || 0} KB)</h3>
          <p className={`status ${processed?.status}`}>
            {processed?.status === "ok" && "🟢 Ready"}
            {processed?.status === "low" && "🟡 Too Small"}
            {processed?.status === "high" && "🔴 Too Large"}
          </p>
          <canvas ref={previewRef} />
        </div>
      </div>

      {advancedMode && (
        <><button onClick={autoFit}>⚡ Auto Fit</button><button onClick={handleReset}>🔄 Reset</button></>)}
      <button onClick={download} disabled={!processed}>⬇ Download</button>
    </div>
  );
}